<template>
  <div class="panel panel-stretch panel-default">
    <div class="panel-heading">
      Huidige woonsituatie
    </div>
    <div class="panel-body">
      <div v-if="!dataLoaded">
        <Skeleton v-bind:loading="true" style="display: block; margin: 1em 0; height: 2em;"></Skeleton>
        <Skeleton v-bind:loading="true" style="display: block; margin: 1em 0; height: 2em;"></Skeleton>
      </div>

      <div v-if="dataLoaded">
        <a-row v-if="hasPartner && hasCustomerProperty" v-bind:gutter="16">
          <a-col v-bind:span="12">
            <a-form-item label="Woont samen">
              <a-radio-group v-model="customerProperty.living_together"
                             button-style="solid"
                             v-bind:disabled="disabled"
                             v-on:change="handleLivingTogetherChange">
                <a-radio-button v-bind:value="true">
                  Ja
                </a-radio-button>
                <a-radio-button v-bind:value="false">
                  Nee
                </a-radio-button>
              </a-radio-group>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row v-if="hasCustomerProperty && hasCurrentLivingCondition" style="margin-bottom: 10px;" v-bind:gutter="16">
          <a-col v-bind:span="12">
            <a-form-item v-bind:label="mainTypeLabel">
              <a-radio-group v-model="currentLivingCondition.type"
                             button-style="solid"
                             v-bind:disabled="loadingEnrichment || currentLivingConditionTypeDisabled"
                             v-on:change="onOccupancyTypeChange">
                <a-radio-button v-for="(occupancyName, occupancyType) in occupancyTypes"
                                v-bind:key="occupancyType"
                                v-bind:value="occupancyType">
                  {{ occupancyName }}
                </a-radio-button>
              </a-radio-group>
            </a-form-item>
          </a-col>
        </a-row>

        <property-input-component ref="propertyInputComponent"
                                  v-bind:key="customer.id + '_' + propertyId"
                                  v-bind:default-property-id="propertyId"
                                  v-bind:emit-events="true"
                                  v-on:property-updated="handlePropertyUpdated"
                                  v-on:property-deleted="handlePropertyDeleted"
                                  v-on:property-selected="handlePropertySelected"
                                  :customer-id="customer.id"
                                  :customer-full-name="customer.full_name"
                                  style="margin: 1.5rem 0;"
        />

        <CurrentLivingConditionEditCustomerComponent
            v-if="hasCustomerProperty && hasCurrentLivingCondition"
            v-bind:key="customer.id + '_' + currentLivingCondition.type"
            v-bind:current-living-condition-prop="currentLivingCondition"
            v-bind:customer-prop="customer"
            v-bind:customer-property-prop="customerProperty"
            v-bind:is-partner="false"
            v-bind:living-together="livingTogether"
            v-bind:mortgage-properties-prop="mortgageProperties"
            v-bind:mortgages-prop="mortgages"
            v-bind:partner-prop="partner"
            v-bind:property-prop="property"
        />

        <template v-if="hasPartner && !livingTogether && hasCustomerProperty">
          <a-row v-if="hasPartnerCustomerProperty && hasPartnerCurrentLivingCondition" style="margin-bottom: 10px;"
                 v-bind:gutter="16">
            <a-col v-bind:span="12">
              <a-form-item v-bind:label="partnerTypeLabel">
                <a-radio-group v-model="partnerCurrentLivingCondition.type"
                               button-style="solid"
                               v-bind:disabled="loadingEnrichment || partnerCurrentLivingConditionTypeDisabled"
                               v-on:change="onPartnerOccupancyTypeChange">
                  <a-radio-button v-for="(occupancyName, occupancyType) in occupancyTypes"
                                  v-bind:key="occupancyType"
                                  v-bind:value="occupancyType">
                    {{ occupancyName }}
                  </a-radio-button>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>

          <property-input-component ref="partnerPropertyInputComponent"
                                    v-bind:key="partner.id + '_' + partnerPropertyId"
                                    v-bind:default-property-id="partnerPropertyId"
                                    v-bind:emit-events="true"
                                    v-on:property-updated="handlePartnerPropertyUpdated"
                                    v-on:property-deleted="handlePartnerPropertyDeleted"
                                    v-on:property-selected="handlePartnerPropertySelected"
                                    @loading-enrichment="handleLoadingEnrichment"
                                    :customer-id="partner.id"
                                    :customer-full-name="partner.full_name"
                                    style="margin: 1.5rem 0;"
          />

          <CurrentLivingConditionEditCustomerComponent
              v-if="hasPartnerCustomerProperty && hasPartnerCurrentLivingCondition"
              v-bind:key="partner.id + '_' + partnerCurrentLivingCondition.type"
              v-bind:current-living-condition-prop="partnerCurrentLivingCondition"
              v-bind:customer-prop="partner"
              v-bind:customer-property-prop="partnerCustomerProperty"
              v-bind:is-partner="true"
              v-bind:living-together="false"
              v-bind:mortgage-properties-prop="partnerMortgageProperties"
              v-bind:mortgages-prop="partnerMortgages"
              v-bind:partner-prop="customer"
              v-bind:property-prop="partnerProperty"
          />
        </template>

        <div v-if="!hasCustomerProperty && loadingCustomerProperty">
          <a-row v-bind:gutter="16">
            <a-col v-bind:span="12">
              <Skeleton height="22px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="110px"></Skeleton>
              <Skeleton height="32px" style="display: block; margin-bottom: 13px;"
                        v-bind:loading="true"></Skeleton>
              <Skeleton height="22px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="110px"></Skeleton>
              <Skeleton height="32px" style="display: block; margin-bottom: 13px;"
                        v-bind:loading="true"></Skeleton>
              <Skeleton height="22px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="95px"></Skeleton>
              <br/>
              <Skeleton height="32px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="100px"></Skeleton>
              <br/>
              <Skeleton height="22px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="170px"></Skeleton>
              <br/>
              <Skeleton height="32px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="250px"></Skeleton>
              <br/>
            </a-col>
            <a-col v-bind:span="12">
              <Skeleton height="22px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="200px"></Skeleton>
              <Skeleton height="32px" style="display: block; margin-bottom: 13px;"
                        v-bind:loading="true"></Skeleton>
              <Skeleton height="22px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="130px"></Skeleton>
              <Skeleton height="32px" style="display: block; margin-bottom: 13px;"
                        v-bind:loading="true"></Skeleton>
              <Skeleton height="22px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="95px"></Skeleton>
              <br/>
              <Skeleton height="32px" style="display: inline-block; margin-bottom: 13px;" v-bind:loading="true"
                        width="100"></Skeleton>
            </a-col>
          </a-row>
          <Skeleton v-bind:loading="true" style="display: block; margin-bottom: 13px; height: 80px;"></Skeleton>
        </div>
      </div>

      <div v-if="dataLoaded && hasCustomerProperty" style="margin-top: 15px;">
        <a-alert
            message="Gewijzigde gegevens worden automatisch opgeslagen"
            show-icon
            type="info"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CurrentLivingConditionEditCustomerComponent from "./EditCustomerComponent";
import customerCurrentLivingConditionApi from "~/api/customer_current_living_condition";
import customerApi from "~/api/customer";
import mortgageApi from "~/api/mortgage";
import customerPropertyApi from "~/api/customer_property";
import editCustomerPropertyComponent from "~/modules/customer_property/components/EditComponent";
import editCustomerCurrentLivingConditionComponent from "./EditComponent";
import createMortgagePropertyComponent from "~/modules/mortgage_property/components/CreateComponent";
import editRentalComponent from "./EditRentalComponent";
import editResidentComponent from "./EditResidentComponent";
import editPropertyComponent from "./EditPropertyComponent";
import { Skeleton } from "vue-loading-skeleton";
import { OccupancyTypeEnum, PropertyPurposeEnum } from "../../../enums";

export default {
  name: "CustomerCurrentLivingConditionNewEditPageComponent",
  components: {
    editCustomerPropertyComponent,
    createMortgagePropertyComponent,
    editCustomerCurrentLivingConditionComponent,
    editRentalComponent,
    editResidentComponent,
    editPropertyComponent,
    Skeleton,
    CurrentLivingConditionEditCustomerComponent
  },
  props: {
    customerId: {
      required: true,
      type: Number
    },
    autoSave: {
      required: false,
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      customer: null,
      partner: null,
      property: null,
      customerProperty: null,
      currentLivingCondition: null,
      partnerMortgages: null,
      partnerMortgageProperties: null,
      partnerProperty: null,
      partnerCustomerProperty: null,
      partnerCurrentLivingCondition: null,
      propertyPersistTimeout: null,
      customerPropertyPersistTimeout: null,
      currentLivingConditionPersistTimeout: null,
      partnerPropertyPersistTimeout: null,
      partnerCustomerPropertyPersistTimeout: null,
      partnerCurrentLivingConditionPersistTimeout: null,
      mortgages: [],
      mortgageProperties: [],
      dataLoaded: false,
      loadingCustomerProperty: false,
      loadingCurrentLivingCondition: false,
      loadingEnrichment: false,
      loadingPartnerCustomerProperty: false,
      loadingPartnerCurrentLivingCondition: false,
      occupancyTypes: {
        rental: "Huur",
        resident: "Inwonend",
        property: "Eigendom",
      },
      propertyPurposes: {
        "keep_living": "Blijven wonen",
        "sell": "Verkopen",
        "sold": "Verkocht",
      },
      types: {
        single_family_home: "Eengezinswoning",
        single_family_house_with_garage: "Eengezinswoning met garage",
        flat_apartment: "Flat/appartement",
        flat_apartment_with_garage: "Flat/appartement met garage",
        shop_house: "Winkel-woonhuis",
        residential_house_with_commercial_space: "Woonhuis met bedrijfsruimte",
        store: "Winkel",
        business_premises: "Bedrijfspand",
        recreational_home: "Recreatiewoning",
        farm: "Boerderij",
        business_collateral: "Zakelijk onderpand",
        agricultural_building_land: "Agrarische grond/bouwgrond",
        garage: "Garage",
        building_plot: "Bouwkavel",
        houseboat: "Woonschip",
        pleasure_craft: "Pleziervaartuig",
      },
      currentLivingConditionIncludes: [
        "customer_property",
        "customer_property.property",
        "customer_property.customer",
        "customer_property.property.mortgage_properties",
        "customer_property.property.mortgage_properties.mortgage",
        "mortgages.mortgage_properties",
        "mortgages"
      ]
    };
  },
  computed: {
    mainTypeLabel() {
      if (this.livingTogether && this.hasPartner) {
        return `Hoe wonen ${this.customerFirstName} en ${this.partnerFirstName}`;
      }

      return `Hoe woont ${this.customerFirstName}`;
    },
    partnerTypeLabel() {
      return `Hoe woont ${this.partnerFirstName}`;
    },
    hasProperty() {
      return this.property !== null;
    },
    hasPartnerProperty() {
      return this.partnerProperty !== null;
    },
    hasCustomer() {
      return this.customer !== null;
    },
    hasPartner() {
      return this.partner !== null;
    },
    hasMortgages() {
      return typeof this.mortgages !== "undefined" && this.mortgages !== null && this.mortgages.length > 0;
    },
    livingTogether() {
      if (!this.hasCustomer || !this.hasPartner || !this.hasCustomerProperty) {
        return false;
      }

      return this.customerProperty.living_together || false;
    },
    propertyId() {
      if (this.hasProperty) {
        return this.property.id;
      }
      return null;
    },
    partnerPropertyId() {
      if (this.hasPartnerProperty) {
        return this.partnerProperty.id;
      }
      return null;
    },
    hasCurrentLivingCondition() {
      return this.currentLivingCondition !== null;
    },
    hasPartnerCurrentLivingCondition() {
      return this.partnerCurrentLivingCondition !== null;
    },
    hasCustomerProperty() {
      return this.customerProperty !== null;
    },
    hasPartnerCustomerProperty() {
      return this.partnerCustomerProperty !== null;
    },
    propertyPurpose() {
      if (this.hasCustomerProperty) {
        return this.customerProperty.property_purpose;
      }

      return null;
    },
    propertyOccupancyType() {
      if (this.hasCurrentLivingCondition) {
        return this.currentLivingCondition.type;
      }
    },
    showRentalFields() {
      if (this.hasCurrentLivingCondition) {
        return this.currentLivingCondition.type === OccupancyTypeEnum.RENTAL;
      }
      return false;
    },
    showResidentFields() {
      if (this.hasCurrentLivingCondition) {
        return this.currentLivingCondition.type === OccupancyTypeEnum.RESIDENT;
      }
      return false;
    },
    showPropertyFields() {
      if (this.hasCurrentLivingCondition) {
        return this.currentLivingCondition.type === OccupancyTypeEnum.PROPERTY;
      }
      return false;
    },
    showPropertyPurposeKeepLivingFields() {
      return this.propertyPurpose === PropertyPurposeEnum.KEEP_LIVING;
    },
    showPropertyPurposeSellFields() {
      return this.propertyPurpose === PropertyPurposeEnum.SELL;
    },
    showPropertyPurposeSoldFields() {
      return this.propertyPurpose === PropertyPurposeEnum.SOLD;
    },
    isMainCustomer() {
      if (this.customer !== null && typeof this.customer !== "undefined") {
        return this.customer.is_main_customer;
      }
    },
    customerName() {
      if (this.hasCustomer) {
        return this.customer.full_name || "Klant";
      }
      return "Klant";
    },
    customerFirstName() {
      if (this.hasCustomer) {
        return this.customer.first_name || "hoofdklant";
      }
      return "hoofdklant";
    },
    partnerName() {
      if (this.hasPartner) {
        return this.partner.full_name || "Partner";
      }
      return "Partner";
    },
    partnerFirstName() {
      if (this.hasPartner) {
        return this.partner.first_name || "partner";
      }
      return "partner";
    },
    disabled() {
      return this.loadingEnrichment || !this.dataLoaded || this.loadingCustomerProperty;
    },
    partnerCurrentLivingConditionTypeDisabled() {
      return this.loadingPartnerCustomerProperty || this.loadingPartnerCurrentLivingCondition;
    },
    currentLivingConditionTypeDisabled() {
      return this.loadingCustomerProperty || this.loadingCurrentLivingCondition;
    },
  },
  mounted() {
    this.initCustomer()
        .then(() => {
          this.initMortgages()
              .then(() => {
                this.initCurrentLivingCondition();
              });
        });
  },
  methods: {
    initMortgages() {
      return mortgageApi.getByCustomer(this.customerId)
          .then(response => {
            this.mortgages = response.data.data;
          })
          .catch();
    },
    handlePropertyDeleted() {
      let customerProperty = this.customerProperty;
      this.property = null;
      this.customerProperty = null;

      customerProperty.is_current = false;

      customerPropertyApi.update(customerProperty)
          .catch();

      this.$nextTick(() => {
        this.$refs.propertyInputComponent.handleShowPropertyCreate();
      });
    },
    handlePropertySelected(value) {
      this.property = value;
      this.loadingCustomerProperty = true;

      customerPropertyApi.findOrCreateCurrentByCustomerAndPropertyAndAddressType(
          this.customer.id,
          this.property.id,
          "visit"
      )
          .then((response) => {
            this.initCurrentLivingConditionByProperty();
          })
          .catch(error => {
          })
          .finally(() => {
            this.loadingCustomerProperty = false;
          });
    },
    handlePartnerPropertyDeleted() {
      let customerProperty = this.partnerCustomerProperty;
      this.partnerProperty = null;
      this.partnerCustomerProperty = null;

      customerProperty.is_current = false;

      customerPropertyApi.update(customerProperty)
          .catch(error => {
          });

      this.$nextTick(() => {
        this.$refs.partnerPropertyInputComponent.handleShowPropertyCreate();
      });
    },
    handlePartnerPropertySelected(value) {
      this.partnerProperty = value;
      this.loadingCustomerProperty = true;

      customerPropertyApi.findOrCreateCurrentByCustomerAndPropertyAndAddressType(
          this.partner.id,
          this.partnerProperty.id,
          "visit"
      )
          .then((response) => {
            this.initPartnerCurrentLivingCondition();
          })
          .finally(() => {
            this.loadingCustomerProperty = false;
          });
    },
    handlePropertyUpdated() {
      this.initCurrentLivingConditionByProperty();
    },
    handlePartnerPropertyUpdated() {
      this.initPartnerCurrentLivingCondition();
    },
    async handleLivingTogetherChange() {
      if (!this.livingTogether) {
        if (this.currentLivingCondition.type === OccupancyTypeEnum.PROPERTY) {
          this.customerProperty.percentage_owner = 100;
        } else {
          this.customerProperty.percentage_owner = null;
        }
      }

      if (this.autoSave) {
        await this.saveCustomerProperty();
      }

      if (!this.livingTogether) {
        await this.initPartnerProperty()
            .then(async () => {
              await this.initPartnerCurrentLivingCondition();

              if (this.partnerCurrentLivingCondition.type === OccupancyTypeEnum.PROPERTY) {
                this.partnerCustomerProperty.percentage_owner = 100;
              } else {
                this.partnerCustomerProperty.percentage_owner = null;
              }

              if (this.autoSave) {
                this.savePartnerCustomerProperty();
              }
            });
      }
    },
    handleLoadingEnrichment(loadingEnrichment) {
      this.loadingEnrichment = loadingEnrichment;
    },
    initCustomer() {
      return customerApi.find(this.customerId, ["partner"])
          .then(response => {
            this.customer = response.data;

            if (typeof this.customer.partner !== "undefined" && this.customer.partner !== null) {
              this.partner = this.customer.partner.data;
            }
          })
          .catch();
    },
    initCurrentLivingCondition() {
      this.loadingCustomerProperty = true;

      customerCurrentLivingConditionApi.findByCustomer(
          this.customerId,
          this.currentLivingConditionIncludes
      )
          .then(async response => {
            this.setCurrentLivingConditionModels(response.data);

            if (this.hasPartner) {
              await this.initPartnerProperty()
                  .then(() => {
                    this.initPartnerCurrentLivingCondition();
                  });
            }
          })
          .catch(error => {
            if (error.response && error.response.status !== 404) {
              return error;
            }
          })
          .finally(() => {
            this.loadingCustomerProperty = false;
            this.dataLoaded = true;
          });
    },
    async initPartnerProperty() {
      return new Promise((resolve, reject) => {
        customerPropertyApi.findCurrentByCustomerAndAddressType(
            this.partner.id,
            "visit",
            ["property"]
        )
            .then(response => {
              this.partnerCustomerProperty = response.data;
              this.partnerProperty = this.partnerCustomerProperty.property.data;
              resolve(this.partnerProperty);
            })
            .catch(async error => {
              if (error.response && error.response.status === 404) {
                await this.assumeLivingTogether();

                resolve(this.partnerProperty);
              } else {
                reject(error);
              }
            });
      });
    },
    async assumeLivingTogether() {
      this.partnerProperty = this.property;
      this.customerProperty.living_together = true;

      await this.handleLivingTogetherChange();
    },
    initPartnerCurrentLivingCondition() {
      return customerCurrentLivingConditionApi.findOrCreateByCustomerAndProperty(
          this.partner.id,
          this.partnerProperty.id,
          this.currentLivingConditionIncludes
      )
          .then(response => {
            this.partnerCurrentLivingCondition = response.data;
            this.partnerMortgages = this.partnerCurrentLivingCondition.mortgages.data;
            this.partnerCustomerProperty = this.partnerCurrentLivingCondition.customer_property.data;
            this.partnerProperty = this.partnerCustomerProperty.property.data;
            this.partnerMortgageProperties = this.partnerProperty.mortgage_properties.data;
          })
          .catch(error => {
          })
          .finally(() => {
            this.dataLoaded = true;
            this.loadingCustomerProperty = false;
          });
    },
    initCurrentLivingConditionByProperty() {
      if (this.propertyId === null) {
        throw new Error("Property not loaded yet");
      }

      this.loadingCustomerProperty = true;

      customerCurrentLivingConditionApi.findOrCreateByCustomerAndProperty(
          this.customerId,
          this.propertyId,
          this.currentLivingConditionIncludes
      )
          .then(async response => {
            this.setCurrentLivingConditionModels(response.data);

            if (this.hasPartner) {
              await this.initPartnerProperty()
                  .then(() => {
                    this.initPartnerCurrentLivingCondition();
                  });
            }
            this.dataLoaded = true;
          })
          .catch(error => {
          })
          .finally(() => {
            this.loadingCustomerProperty = false;
          });
    },
    setCurrentLivingConditionModels(currentLivingCondition) {
      this.currentLivingCondition = currentLivingCondition;

      if (this.currentLivingCondition.hasOwnProperty("customer_property")
          && typeof this.currentLivingCondition.customer_property !== "undefined"
          && typeof this.currentLivingCondition.customer_property !== null
      ) {
        this.customerProperty = this.currentLivingCondition.customer_property.data;
        this.property = this.customerProperty.property.data;
        this.mortgageProperties = this.property.mortgage_properties.data;
      }
    },
    async onOccupancyTypeChange() {
      if (this.currentLivingCondition.type === OccupancyTypeEnum.PROPERTY) {
        this.customerProperty.owns_property = true;
        this.customerProperty.percentage_owner = this.customerProperty.percentage_owner || ((this.hasPartner && this.livingTogether) ? 50 : 100);
      } else {
        this.customerProperty.owns_property = false;
      }

      await this.onCustomerPropertyChange();
      await this.onCurrentLivingConditionChange();

      if (this.hasPartner) {
        await this.onPartnerOccupancyTypeChange();
      }
    },
    async onPartnerOccupancyTypeChange() {
      if (this.partnerCurrentLivingCondition.type === OccupancyTypeEnum.PROPERTY) {
        this.partnerCustomerProperty.owns_property = true;
        this.partnerCustomerProperty.percentage_owner = this.partnerCustomerProperty.percentage_owner || (this.livingTogether ? 50 : 100);
      } else {
        this.partnerCustomerProperty.owns_property = false;
      }

      await this.onPartnerCustomerPropertyChange();
      await this.onPartnerCurrentLivingConditionChange();
    },
    async onCustomerPropertyChange() {
      this.loadingCustomerProperty = true;
      await this.saveCustomerProperty();
      this.loadingCustomerProperty = false;
    },
    async onPartnerCustomerPropertyChange() {
      this.loadingPartnerCustomerProperty = true;
      await this.savePartnerCustomerProperty();
      this.loadingPartnerCustomerProperty = false;
    },
    async onCurrentLivingConditionChange() {
      this.loadingCurrentLivingCondition = true;
      await this.saveCurrentLivingCondition();
      this.loadingCurrentLivingCondition = false;
    },
    async onPartnerCurrentLivingConditionChange() {
      this.loadingPartnerCurrentLivingCondition = true;
      await this.savePartnerCurrentLivingCondition();
      this.loadingPartnerCurrentLivingCondition = false;
    },
    async saveCurrentLivingCondition() {
      let timeout,
          promise;
      clearTimeout(this.currentLivingConditionPersistTimeout);

      [timeout, promise] = this.timeout(async () => {
        customerCurrentLivingConditionApi.update(this.currentLivingCondition)
            .catch();
      }, 500);

      this.currentLivingConditionPersistTimeout = timeout;

      return promise;
    },
    async savePartnerCurrentLivingCondition() {
      let timeout,
          promise;
      clearTimeout(this.partnerCurrentLivingConditionPersistTimeout);

      [timeout, promise] = this.timeout(async () => {
        customerCurrentLivingConditionApi.update(this.partnerCurrentLivingCondition)
            .catch();
      }, 500);

      this.partnerCurrentLivingConditionPersistTimeout = timeout;

      return promise;
    },
    async saveCustomerProperty() {
      let timeout,
          promise;
      clearTimeout(this.customerPropertyPersistTimeout);

      [timeout, promise] = this.timeout(async () => {
        customerPropertyApi.update(this.customerProperty)
            .catch();
      }, 500);

      this.customerPropertyPersistTimeout = timeout;

      return promise;
    },
    async savePartnerCustomerProperty() {
      let timeout,
          promise;
      clearTimeout(this.partnerCustomerPropertyPersistTimeout);

      [timeout, promise] = this.timeout(async () => {
        customerPropertyApi.update(this.partnerCustomerProperty)
            .catch();
      }, 500);

      this.partnerCustomerPropertyPersistTimeout = timeout;
      return promise;
    },
    timeout(callback, ms) {
      let timeout = null;
      const promise = new Promise(resolve => {
        timeout = setTimeout(async () => {
          await callback();
          resolve();
        }, ms);
      });

      return [timeout, promise];
    }
  }
};
</script>

<style scoped>
</style>
